import { useMemo, useState } from 'react';
import CreatableSelect from 'react-select/creatable';
import { useTranslation } from 'react-i18next';
import type { CatalogCategoryKindType, DirCatalogCategoryType } from '@econtractor/types';
import { useTheme } from 'context/ThemeContext';
import { useSettings } from 'context/SettingsContext';
import { useToast } from 'context/ToastContext';
import { selectStylesDark, selectStylesLigth } from 'utils/helpers/structs/selectStyles';
import * as apiSettings from 'api/apiSettings';
import { CATALOG_DIR_I18N_NS, catalogDirApi } from './model/catalogDirApi';
import type { CatalogSelectOption } from './types';

interface CatalogCategorySelectProps {
    kind: CatalogCategoryKindType;
    value?: number | null;
    onChange: (option: CatalogSelectOption | null) => void;
    disabled?: boolean;
    inputId?: string;
}

const DIRS_KEY: Record<CatalogCategoryKindType, 'ProductCategory' | 'ProductSetCategory' | 'ServiceCategory'> = {
    product: 'ProductCategory',
    set: 'ProductSetCategory',
    service: 'ServiceCategory',
};

const CatalogCategorySelect = ({
    kind,
    value,
    onChange,
    disabled = false,
    inputId,
}: CatalogCategorySelectProps) => {
    const ns = CATALOG_DIR_I18N_NS[kind];
    const { t } = useTranslation([ns, 'common']);
    const { darkTheme } = useTheme();
    const { localSettings, dispatchLocalSettings } = useSettings();
    const { showToast } = useToast();
    const [creating, setCreating] = useState(false);
    const [selectTheme] = useState(darkTheme ? selectStylesDark : selectStylesLigth);

    const options = useMemo<CatalogSelectOption[]>(() => {
        const items = (localSettings?.DIRS?.[DIRS_KEY[kind]] || []) as DirCatalogCategoryType[];
        return items
            .filter((item) => !item.hidden || item.ID === value)
            .map((item) => ({ value: item.ID, label: item.categoryName }));
    }, [kind, localSettings?.DIRS, value]);

    const handleCreate = async (inputValue: string) => {
        const name = String(inputValue || '').trim();
        if (!name || creating) return;
        setCreating(true);
        const res = await catalogDirApi[kind].create(name);
        if (res?.stat && res.id) {
            const settings = await apiSettings.getSettings();
            if (settings) dispatchLocalSettings({ type: 'SET', settings });
            onChange({ value: res.id, label: res.name || name });
            setCreating(false);
            return;
        }
        if (res?.errCode === 'DUPLICATE_NAME') {
            const existing = options.find((opt) => opt.label.trim().toLowerCase() === name.toLowerCase());
            if (existing) onChange(existing);
            else showToast(t('dirs.messDuplicate'), 'danger');
            setCreating(false);
            return;
        }
        if (res?.errCode === 'EMPTY_NAME') {
            showToast(t('dirs.messEmpty'), 'danger');
        } else if (res?.errCode === 'NAME_TOO_LONG') {
            showToast(t('dirs.messTooLong'), 'danger');
        } else {
            showToast(t('common:messError'), 'danger');
        }
        setCreating(false);
    };

    return (
        <CreatableSelect<CatalogSelectOption, false>
            inputId={inputId}
            options={options}
            isClearable
            isDisabled={disabled || creating}
            isLoading={creating}
            placeholder={t('fields.category')}
            formatCreateLabel={(input) => t('createOption', { name: input })}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            menuPosition="fixed"
            styles={{
                ...selectTheme,
                menuPortal: (base) => ({
                    ...base,
                    zIndex: 2000,
                }),
            }}
            className="w-100"
            value={options.find((opt) => opt.value === value) || null}
            onChange={onChange}
            onCreateOption={(input) => {
                void handleCreate(input);
            }}
        />
    );
};

export default CatalogCategorySelect;
